import React, { useState } from 'react'

import { BoxIcon, XIcon } from 'lucide-react'

import { Box, IconButton, Stack, Typography, alpha, useTheme } from '@mui/material'

import { FieldLabel, ProductModel, Skeleton, fieldBorderColor, fieldFill } from '@repo/ui'

import { useSnackbar } from 'contexts'
import {
  PRODUCT_MODEL_ACCEPT,
  PRODUCT_MODEL_MAX_SIZE_MB,
  uploadProductModel,
} from 'services/mediaApi'

// Direct path, NOT the `components` barrel: this file is itself re-exported by that
// barrel, so going through it would close a cycle (components → input → ProductModelField
// → components) and resolve to undefined at module-init time — an empty render with no
// error pointing here. Same reasoning ScanMediaField and FileUploadField both record.
import { FileUploadField } from './FileUploadField'

/**
 * Dropzone / preview height.
 *
 * 220 rather than ScanMediaField's 140. A 3D model is the thing being judged here, not a
 * reference image glanced at — the whole point of the field is to see whether the model
 * is right way up, right scale and not visibly broken before it reaches a customer, and
 * 140px of a spinning object is too small to tell. Still under FileUploadField's 268px
 * gallery default, so the card does not dominate the form.
 */
const FIELD_HEIGHT = 220

/**
 * The dropzone's empty-state glyph, wrapped rather than passed straight through.
 *
 * `FileUploadField` declares `icon` as `ComponentType<{size?: number; strokeWidth?:
 * number}>`, and a raw `LucideIcon` is not assignable to it — the pnpm store holds two
 * copies of `lucide-react`, so the `LucideIcon` this file imports is nominally a
 * different type from the one in the field's own declaration. Same component at runtime,
 * different declaration file; the same dual-copy problem the `fieldTheme` cast below
 * documents for `@mui/material`, and the one `TagsShopProductPage` carries ~9 live
 * errors for.
 *
 * A four-line wrapper rather than an `as` cast, because it states the contract instead
 * of suppressing the check: this really does accept only those two props, so if the
 * field's signature changes the compiler still has something true to check against.
 * ScanMediaField does not hit this only because its glyph is a local component already.
 */
const ModelGlyph = ({ size, strokeWidth }: { size?: number; strokeWidth?: number }) => (
  <BoxIcon size={size} strokeWidth={strokeWidth} />
)

export interface ProductModelFieldProps {
  label?: string
  /** The stored GLB URL, or null/undefined when nothing has been uploaded. */
  value?: string | null
  /** Called with the uploaded URL, or `null` when the user clears it. */
  onChange: (url: string | null) => void
  disabled?: boolean
  /** Inline content beside the label — in practice an `InfoTooltip`. */
  labelAdornment?: React.ReactNode
}

/**
 * A product's 3D model (a GLB), uploaded and previewed LIVE in the form.
 *
 * ## Why this is a sibling of `ScanMediaField` rather than a prop on it
 *
 * §36 says the second hand-roll of a unit becomes a shared component, so the choice not
 * to extend `ScanMediaField` is recorded rather than assumed. What the two actually
 * share is already shared, and it is most of the mass: `FileUploadField` (the dropzone),
 * `FieldLabel`, `Skeleton`, and the `fieldBorderColor`/`fieldFill` chrome helpers (§31).
 * This file is the COMPOSITION, and every line of it that is not boilerplate differs —
 * the accepted format, the size cap, the upload endpoint, the empty-state copy, and
 * above all the preview, which is a live WebGL canvas rather than an `<img>`/`<video>`
 * swap. Folding them together would mean passing all five as parameters, which is the
 * same code behind an indirection, and it would put a `@google/model-viewer` import on
 * the path of every scan-media field that will never render one.
 *
 * ## The preview is the real work, and it is deliberately the SHARED viewer
 *
 * `ProductModel` from `@repo/ui` — the same component the consumer certificate page and
 * the product page render. That is the point: a brand checking a model here is looking
 * at the exact renderer, lighting and framing a customer will get, so "it looked fine in
 * the form" cannot diverge from what ships. A second, form-only preview is precisely how
 * that divergence starts.
 *
 * `autoRotate` is ON here even though this is an editing context, because the spin is a
 * property being reviewed: a model whose pivot is off-centre or whose up-axis is wrong
 * looks correct while static and obviously wrong the moment it turns.
 */
export const ProductModelField = ({
  label,
  value,
  onChange,
  disabled = false,
  labelAdornment,
}: ProductModelFieldProps) => {
  const theme = useTheme()
  /*
    ONE cast, for the field-chrome helpers below — the same workaround ScanMediaField and
    FileUploadField both document: the pnpm store holds two @mui/material copies, so the
    `Theme` this file's `useTheme()` returns is nominally incompatible with the `Theme` in
    @repo/ui's helper signatures. Same object at runtime, different declaration file.
  */
  const fieldTheme = theme as Parameters<typeof fieldFill>[0]

  const { open: notification } = useSnackbar()

  const [isUploading, setIsUploading] = useState(false)
  /*
    Bumped to REMOUNT FileUploadField, which owns its own file list internally. Without it
    a failed upload leaves the rejected file sitting in the dropzone, so the user is
    looking at a file the server never accepted and has no way to retry the same one.
  */
  const [dropzoneKey, setDropzoneKey] = useState(0)

  const handleFiles = async (files: File[]) => {
    // FileUploadField emits `[]` on mount, and again after the remount below. Neither is
    // a user clearing anything — the clear affordance is the X on the preview — so an
    // empty list is ignored rather than treated as `onChange(null)`.
    const file = files[0]
    if (!file || isUploading) return

    setIsUploading(true)
    try {
      const { path } = await uploadProductModel(file)
      onChange(path)
    } catch (error: any) {
      notification({
        type: 'error',
        title: error?.message || "Couldn't upload that model",
      })
      // Previous value stays intact — a failed upload replaces nothing.
      setDropzoneKey((k) => k + 1)
    } finally {
      setIsUploading(false)
    }
  }

  const handleClear = () => {
    onChange(null)
    // The dropzone that reappears must be empty; it still holds whatever was dropped last.
    setDropzoneKey((k) => k + 1)
  }

  return (
    // ONE element in the layout (§31): label, control and helper live in a single
    // container, so a parent `<Stack spacing={n}>` separates FIELDS and never gets
    // between this label and its control.
    <Box sx={{ width: '100%' }}>
      {/*
        The bare string stays the no-adornment path on purpose: `FieldLabel` styles a
        STRING adornment itself (12px, muted) and cannot style one buried in a Stack.
        Same composition as ScanMediaField — see its note for the line-height trap.
      */}
      <FieldLabel
        label={label}
        adornment={
          labelAdornment
            ? (
              <Stack direction="row" alignItems="center" spacing={1}>
                <Box
                  component="span"
                  sx={{ fontSize: 12, color: 'text.secondary', lineHeight: 'inherit' }}
                >
                  (optional)
                </Box>
                {labelAdornment}
              </Stack>
            )
            : '(optional)'
        }
      />

      {value ? (
        <Box
          sx={{
            position: 'relative',
            height: FIELD_HEIGHT,
            width: '100%',
            overflow: 'hidden',
            borderRadius: 2,
            // Same rest border every other field carries (§31), from the shared helper
            // rather than a local token that would drift.
            border: `1px solid ${fieldBorderColor(fieldTheme)}`,
            /*
             * `fieldFill`, same as the dropzone it replaces and the fields above it.
             * The plate rule is "take the surface of the things beside you", and
             * beside this one those things are FIELDS, not cards — so the field
             * chrome is the right source here (§31) even though the detail page's
             * viewer reads `background.paper`. Both are the same rule applied to
             * different neighbours.
             */
            backgroundColor: fieldFill(fieldTheme),
          }}
        >
          <ProductModel
            src={value}
            title={label}
            height={FIELD_HEIGHT}
            // No frame of its own: the bordered box above already is the frame, and
            // `panel` would draw a second one inside it.
            surface="bare"
            // The hint belongs on a customer-facing surface. Here the person already
            // knows they can drag it, and the line would compete with the field's own
            // label for the same two lines of vertical space.
            showHint={false}
          />

          {/* X, not a trash can (§38): this severs the association between the record and
              a stored file. Nothing is destroyed and it is cheap to undo by uploading
              again, so it takes no confirmation either. */}
          {!disabled && (
            <IconButton
              onClick={handleClear}
              aria-label="Remove this 3D model"
              size="small"
              sx={{
                position: 'absolute',
                top: 8,
                right: 8,
                // Above the model-viewer canvas, which creates its own stacking context.
                zIndex: 1,
                backgroundColor: alpha(theme.palette.common.black, 0.45),
                color: theme.palette.common.white,
                '&:hover': { backgroundColor: alpha(theme.palette.common.black, 0.65) },
              }}
            >
              <XIcon size={18} strokeWidth={1} />
            </IconButton>
          )}
        </Box>
      ) : isUploading ? (
        // §19 — footprint parity, not a spinner in an empty box: the placeholder occupies
        // the exact space the dropzone (and then the preview) occupies, so nothing shifts
        // when the upload lands.
        <Stack spacing={2}>
          <Skeleton variant="rounded" width="100%" height={FIELD_HEIGHT} />
          <Typography variant="body4" color="text.secondary">
            Uploading…
          </Typography>
        </Stack>
      ) : (
        <Box
          sx={{
            ...(disabled && { opacity: 0.5, pointerEvents: 'none' }),
          }}
          aria-disabled={disabled || undefined}
        >
          <FileUploadField
            key={dropzoneKey}
            acceptFiles={PRODUCT_MODEL_ACCEPT}
            /*
              A wireframe cube — the subject of the field, following the same rule
              ScanMediaField's TagLocationIcon records: the empty state should say WHAT to
              put here, not merely that something can be dropped. Generic upload-cloud
              would say the latter.
            */
            icon={ModelGlyph}
            // Names the format, because this is the one field in the form where the file
            // someone first reaches for (a .gltf, or a .usdz "for iPhone") is the wrong
            // one. The server's refusal explains both; saying it up front avoids it.
            description="GLB 3D model"
            maxFiles={1}
            maxSizeMb={PRODUCT_MODEL_MAX_SIZE_MB}
            showMaxFileSize
            height={FIELD_HEIGHT}
            galleryTitle="3D model"
            onFilesChange={(files) => {
              // FileUploadField hands back its own wrapper ({id, file, …}), not native
              // Files — same unwrap as ScanMediaField and useProductMutations.
              void handleFiles(
                (files as unknown as { file: File }[]).map((wrapper) => wrapper.file),
              )
            }}
          />
        </Box>
      )}
    </Box>
  )
}
